import { type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { AppLoader } from './AppLoader'

/** Gates a single route on the signed-in user holding a specific Permission
 *  string (e.g. "screens:create") — owner/admin/superuser always pass (see
 *  usePermissions.can). Use this for destination pages reachable only via a
 *  UI entry point that's already hidden for the role (e.g. a "Create" nav
 *  button) — the button being hidden doesn't stop direct navigation to the
 *  URL, so the page itself needs its own gate too.
 *
 * Unlike RequireAppAccess/RequireOrgEntitlement (whole-app/whole-org
 * lockouts, which show a dedicated denial page), a missing single
 * capability just silently redirects to `redirectTo` — the user still has
 * access to the app, they just can't reach this one action, matching how
 * the entry point they didn't see would have behaved. */
export default function RequirePermission({
  permission, redirectTo, children,
}: {
  permission: string
  redirectTo: string
  children: ReactNode
}) {
  const { user, loading } = useAuth()
  const { can } = usePermissions()

  // /auth/me hasn't resolved yet — avoid bouncing away before we actually
  // know the user's permissions.
  if (loading || !user) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <AppLoader />
      </div>
    )
  }

  if (!can(permission)) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}
